import type {
  IMediaTranscriptMeta,
  IRecorderSessionSaveBody,
  ITranscriptSegment,
  ThumbnailView,
} from "@speakai/shared";
import type { ApiHttpClient, RequestOptions } from "../interfaces/api-http-client";
import type {
  JobRevision,
  MediaListFilters,
  MediaListResponse,
  MediaDetail,
  MediaTranscriptResponse,
  SourceTranscriptResponse,
  UploadRecordingPayload,
  UploadRecordingResponse,
} from "./types";

export function createMediaApi(http: ApiHttpClient) {
  return {
    // -- List / detail / transcript reads --------------------------------

    /**
     * Fetch paginated media list with optional filters.
     * When filterList is provided, uses POST /media/library. Otherwise GET /media.
     */
    getMediaList(filters: Omit<MediaListFilters, "enabled"> = {}) {
      const {
        page = 0,
        pageSize = 25,
        status = "2",
        query = "",
        filterType,
        sortBy = "createdAt",
        sortDir = "desc",
        from,
        to,
        folderId,
        isFavorites,
        filterList,
      } = filters;

      const params = new URLSearchParams();
      params.set("page", String(page));
      params.set("pageSize", String(pageSize));
      params.set("filterMedia", status);
      const filterName =
        filterType && filterType !== "all" ? `${filterType}:${query}` : query;
      params.set("filterName", filterName);
      params.set("sortBy", `${sortBy}:${sortDir}`);

      if (from) params.set("from", from);
      if (to) params.set("to", to);
      if (folderId) params.set("folderId", folderId);
      if (isFavorites) params.set("isFavorites", "true");

      if (filterList && filterList.length > 0) {
        return http.post<MediaListResponse>(
          `/media/library?${params.toString()}`,
          { filters: filterList },
        );
      }

      return http.get<MediaListResponse>(`/media?${params.toString()}`);
    },

    getMediaDetail(id: string) {
      return http.get<MediaDetail>(`/media/insight/${id}`);
    },

    /** In-app transcript segments for a media item. */
    getTranscript(mediaId: string, options?: RequestOptions) {
      return http.get<MediaTranscriptResponse>(`/media/transcript/${mediaId}`, undefined, options);
    },

    getSourceTranscript(mediaId: string) {
      return http.get<SourceTranscriptResponse>(
        `/media/transcript/${mediaId}/source`,
      );
    },

    /**
     * Fetch the WebVTT caption ownerId for a media item.
     * Server `GET /media/caption/:mediaId` (re)generates the `.vtt` file and
     * returns `{ userId }` — callers build the absolute caption URL from it.
     */
    getMediaCaption(mediaId: string | undefined) {
      return http.get<{ userId: string }>(`/media/caption/${mediaId}`);
    },

    getTranscriptRevisions(mediaId: string) {
      return http.get<JobRevision[]>(
        `/media/transcript/${encodeURIComponent(mediaId)}/revisions`,
      );
    },

    /** Fetch a single saved transcript revision's segments. */
    getTranscriptRevisionSegments(revisionId: string) {
      return http.get<ITranscriptSegment[]>(
        `/media/transcript/revisions/${encodeURIComponent(revisionId)}`,
      );
    },

    /**
     * Transcript-editor-page variant of the media/insight fetch: same
     * endpoint as `getMediaDetail`, narrower response shape (transcript only).
     */
    getMediaEditorTranscript(mediaId: string) {
      return http.get<
        {
          insight: { transcript: ITranscriptSegment[] };
        } & Partial<IMediaTranscriptMeta>
      >(`/media/insight/${mediaId}`);
    },

    // -- Single-media mutations -------------------------------------------

    updateMedia(params: {
      mediaId: string;
      name?: string;
      description?: string;
      folderId?: string;
      tags?: string[];
      remark?: string;
      status?: string;
      manageBy?: string;
      createdAt?: string;
    }) {
      return http.put<{ status: string; message: string }>(
        `/media/${params.mediaId}`,
        params,
      );
    },

    deleteMedia(mediaId: string) {
      return http.delete<{ status: string; message: string }>(
        `/media/${mediaId}`,
      );
    },

    /** `PUT /media/:mediaId/thumbnail` with a JPEG, PNG or WebP image (multipart field `image`). */
    setMediaThumbnailImage(mediaId: string, image: Blob) {
      const formData = new FormData();
      formData.append("image", image);
      return http.upload<{ thumbnail: ThumbnailView }>(
        `/media/${encodeURIComponent(mediaId)}/thumbnail`,
        formData,
        "PUT",
      );
    },

    /** `PUT /media/:mediaId/thumbnail` asking for the video frame at `seekSec`; answers 202 with `{ thumbnail: { pending: true } }`. */
    requestMediaThumbnailFrame(mediaId: string, seekSec: number) {
      return http.put<{ thumbnail: ThumbnailView }>(
        `/media/${encodeURIComponent(mediaId)}/thumbnail`,
        { seekSec },
      );
    },

    moveMedia(params: { mediaIds: string[]; folderId: string }) {
      return http.put<{ status: string; message: string }>(
        `/media/move`,
        params,
      );
    },

    /** Register a recording already uploaded direct-to-S3 against the signed-in
     *  user's own account (see `UploadRecordingPayload` in ./types). */
    uploadRecording(payload: UploadRecordingPayload) {
      return http.post<UploadRecordingResponse>(
        "/media/upload/recording",
        payload,
      );
    },

    // -- Bulk mutations -----------------------------------------------------

    bulkDeleteMedia(validIds: string[]) {
      return http.post<{ status: string; message: string }>(
        `/media/delete`,
        validIds,
      );
    },

    /** Bulk re-analyze (all signals) — caller has already validated/capped `validIds`. */
    bulkReanalyzeMedia(validIds: string[]) {
      return http.post<{ status: string; message: string }>(
        "/media/reAnalyzeMultiple",
        {
          mediaIds: validIds,
          isInsights: true,
          isSentiment: true,
          isFillerWords: true,
          isEmbeddings: true,
        },
      );
    },

    /** Remark-only bulk update — dedicated endpoint, no `name` required. */
    updateMediaRemark(mediaId: string, remark: string | undefined) {
      return http.put<{ status: string; message: string }>(
        `/media/remark/${encodeURIComponent(mediaId)}`,
        { remark },
      );
    },

    /** Tag (and optionally remark) bulk update — server requires `name`. */
    updateMediaTagsAndRemark(
      mediaId: string,
      body: {
        mediaId: string;
        name: string;
        tags?: string[];
        remark?: string;
      },
    ) {
      return http.put<{ status: string; message: string }>(
        `/media/${encodeURIComponent(mediaId)}`,
        body,
      );
    },

    // -- Export -------------------------------------------------------------

    /** Bulk export via the mutation hook path (`useExportMedia`). */
    exportMediaMultiple(
      mediaType: string,
      body: {
        fileType: string;
        mediaIds: string[];
        isSpeakerNames: boolean;
        isTimeStamps: boolean;
        isInsightVisualized: boolean;
        isSpeakerEmail: boolean;
        isRedacted: boolean;
        redactedCategories: string[];
        isMerged: boolean;
        folderId: string;
      },
    ) {
      return http.postBlob(
        `/${encodeURIComponent(mediaType)}/exportMultiple`,
        body,
        { trackingCategory: "media" },
      );
    },

    /** Single-media export (export side panel). */
    exportMediaSingle(
      apiMediaType: string,
      mediaId: string,
      fileType: string,
      body: {
        isSpeakerNames: boolean;
        isSpeakerEmail: boolean;
        isTimeStamps: boolean;
        isInsightVisualized: boolean;
        isRedacted: boolean;
        redactedCategories: string[];
      },
    ) {
      return http.postBlob(
        `/${apiMediaType}/export/${mediaId}/${fileType}`,
        body,
      );
    },

    /** Bulk/folder export (export side panel — separate call site from `exportMediaMultiple`). */
    exportMediaMultipleFromPanel(
      apiMediaType: string,
      body: {
        fileType: string;
        mediaIds: string[];
        isSpeakerNames: boolean;
        isSpeakerEmail: boolean;
        isTimeStamps: boolean;
        isInsightVisualized: boolean;
        isRedacted: boolean;
        redactedCategories: string[];
        folderId: string | undefined;
        isMerged: boolean;
      },
    ) {
      return http.postBlob(`/${apiMediaType}/exportMultiple`, body);
    },

    // -- Reanalyze / retranscribe --------------------------------------------

    /**
     * Full re-analyze (all signals) for a single media item.
     * Server route: GET /v1/media/reanalyze/:mediaId — same endpoint as
     * `reanalyzeInsights`, just with all flags = true.
     */
    reanalyzeMediaFull(mediaId: string) {
      const searchParams = new URLSearchParams({
        isInsights: "true",
        isSentiment: "true",
        isFillerWords: "true",
        isEmbeddings: "true",
      });
      return http.get<{ status: string; message: string }>(
        `/media/reanalyze/${encodeURIComponent(mediaId)}?${searchParams.toString()}`,
      );
    },

    /** Re-analyze insights for a single media item with granular options. */
    reanalyzeInsights(params: {
      mediaId: string;
      isInsights: boolean;
      isSentiment: boolean;
      isFillerWords: boolean;
      isEmbeddings?: boolean;
    }) {
      const { mediaId, isInsights, isSentiment, isFillerWords, isEmbeddings } =
        params;
      const searchParams = new URLSearchParams();
      searchParams.set("isInsights", String(isInsights));
      searchParams.set("isSentiment", String(isSentiment));
      searchParams.set("isFillerWords", String(isFillerWords));
      if (isEmbeddings !== undefined) {
        searchParams.set("isEmbeddings", String(isEmbeddings));
      }
      return http.get<{ status: string; message: string }>(
        `/media/reanalyze/${encodeURIComponent(mediaId)}?${searchParams.toString()}`,
      );
    },

    /** Re-transcribe a media item in a new language — creates new media. */
    retranscribeMedia(params: { mediaId: string; targetLanguage: string }) {
      return http.post<{ status: string; data: { mediaId: string } }>(
        "/media/retranscribe",
        params,
      );
    },

    /** Re-upload a text-type media file stuck in 'preparing' for >1 day. */
    reuploadText(mediaId: string) {
      return http.post(`/text/reupload`, { mediaId });
    },

    // -- Favorites ------------------------------------------------------------

    updateFavorite(params: { mediaIds: string[]; isFavorite: boolean }) {
      return http.post<{ status: string; message: string }>(
        `/media/favorites`,
        params,
      );
    },

    // -- Recording session log ------------------------------------------------

    saveRecordingSession(body: IRecorderSessionSaveBody | { sessionId: string; mediaId: string }, options?: RequestOptions) {
      return http.post<{ sessionId: string }>("/media/session", body, undefined, options);
    },
  };
}
